import { track } from '@eventbrite/datalayer-library';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { Badge, ChevronDownIcon, Typography } from '@eventbrite/marmalade';
import { getExperiment, logEvent } from '@eventbrite/statsig';
import React, { useEffect, useState } from 'react';
import { CAPACITY_DROPDOWN_FEATURE } from './pricingAndPackagingUtils';
import { SparklingSvg } from './SVGs/SparklingSvg';

import styles from './PricingCard.module.scss';

interface PlanOptionsProps {
    label: string;
    icon: JSX.Element;
    cost: string;
    discountedCost: string | null;
    heapId: string;
}

interface PerksProps {
    icon: JSX.Element;
    text: GenericLazyString;
}

interface CTAProps {
    link: string;
    icon?: JSX.Element;
    text: GenericLazyString;
    heapId: string;
}
interface PricingCardProps {
    isRecommended: boolean;
    planName: GenericLazyString;
    subtext: GenericLazyString;
    cost: string;
    currencyCode: string | null;
    discountedCost: string | null;
    costText: GenericLazyString;
    selectedDropdownOption?: string;
    planOptions: PlanOptionsProps[];
    optionsHeapId?: string;
    statsigEventName: string;
    perks: PerksProps[];
    cta: CTAProps;
}

export const getOptionCostDetails = (
    option: string,
    planOptions: PlanOptionsProps[],
) => {
    const planOption = planOptions.find((opt) => opt.label === option);
    if (planOption) {
        return {
            optionCost: planOption.cost,
            optionDiscountedCost: planOption.discountedCost,
        };
    }
    return null;
};

export const PricingCard: React.FunctionComponent<PricingCardProps> = ({
    isRecommended = false,
    planName,
    subtext,
    cost = 0,
    currencyCode = '',
    discountedCost,
    costText,
    selectedDropdownOption,
    planOptions,
    optionsHeapId,
    statsigEventName,
    perks,
    cta,
}) => {
    const [selectedOption, setSelectedOption] = useState(
        selectedDropdownOption,
    );
    const [currentCost, setCurrentCost] = useState(cost);
    const [currentDiscountedCost, setCurrentDiscountedCost] =
        useState(discountedCost);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showCapacityDropdown, setShowCapacityDropdown] = useState<
        null | boolean
    >(null);

    useEffect(() => {
        const experiment = getExperiment(
            CAPACITY_DROPDOWN_FEATURE.experimentName,
            CAPACITY_DROPDOWN_FEATURE.param,
            CAPACITY_DROPDOWN_FEATURE.defaultValue,
        );
        setShowCapacityDropdown(experiment == 'show');
    }, []);

    useEffect(() => {
        const eventName = 'OranizerPricingAllViewPageVariant';
        const eventData = { showCapacityDropdown: showCapacityDropdown };
        track({
            eventName,
            eventData,
        });
    }, [showCapacityDropdown]);

    const handleOptionClick = (option: string) => {
        setSelectedOption(option);
        setDropdownOpen(false);
        const costs = getOptionCostDetails(option, planOptions);
        if (costs) {
            setCurrentCost(costs.optionCost);
            setCurrentDiscountedCost(costs.optionDiscountedCost);
        }
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const showCostSection = showCapacityDropdown
        ? true
        : isRecommended
        ? true
        : false;

    return (
        <div className={isRecommended ? styles.recommended_card : styles.card}>
            <div className={styles.card_content}>
                <div className={styles.plan_div}>
                    <div className={styles.title_section}>
                        <div className={styles.title_div}>
                            <div
                                className={
                                    isRecommended
                                        ? styles.recommended_title
                                        : styles.title
                                }
                            >
                                {planName}
                            </div>
                        </div>

                        {isRecommended && (
                            <Badge
                                variant="opportunity"
                                bordered="false"
                                className={styles.recommended_badge}
                            >
                                {gettext('Recommended')}
                            </Badge>
                        )}
                    </div>
                    <div className={styles.benefits_list_div}>
                        <Typography
                            variant="body-md-bold"
                            className={styles.subtext}
                        >
                            {subtext}
                        </Typography>
                        <div className={styles.benefits_list}>
                            {perks.map((perk, i) => {
                                return (
                                    <div className={styles.benefit_div} key={i}>
                                        {perk.icon}
                                        <Typography
                                            variant="body-sm"
                                            className={styles.benefit}
                                        >
                                            {perk.text}
                                        </Typography>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className={styles.price_and_cta_div}>
                    {showCostSection && currentCost && (
                        <div className={styles.price_detail_section}>
                            <div className={styles.dropdown_section}>
                                {showCapacityDropdown ? (
                                    <Typography variant="body-md-bold">
                                        {gettext('Total tickets per event')}
                                    </Typography>
                                ) : (
                                    <Typography variant="body-caption">
                                        {gettext(
                                            'Monthly subscription starting at',
                                        )}
                                    </Typography>
                                )}

                                {showCapacityDropdown && (
                                    <div
                                        className={styles.selected_option}
                                        onClick={toggleDropdown}
                                        data-heap-id={optionsHeapId}
                                    >
                                        <Typography
                                            color="#3659E3"
                                            variant="body-md-bold"
                                        >
                                            {selectedOption}
                                        </Typography>
                                        <ChevronDownIcon
                                            size="xsmall"
                                            color="control"
                                        />
                                    </div>
                                )}
                            </div>
                            {dropdownOpen && (
                                <div className={styles.dropdown_container}>
                                    {planOptions.map((option) => {
                                        return (
                                            <div
                                                className={
                                                    styles.dropdown_option
                                                }
                                                onClick={() =>
                                                    handleOptionClick(
                                                        option.label,
                                                    )
                                                }
                                                key={option.label}
                                                data-heap-id={option.heapId}
                                            >
                                                <div
                                                    className={
                                                        styles.plan_icon_div
                                                    }
                                                >
                                                    {option.icon}
                                                </div>
                                                <Typography
                                                    variant="body-md-bold"
                                                    color="#3A3247"
                                                >
                                                    {option.label}
                                                </Typography>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {currentCost != 'Free' && (
                                <Typography variant="body-md-bold">
                                    {currencyCode}
                                </Typography>
                            )}
                            <div className={styles.price_section}>
                                <div
                                    className={
                                        currentDiscountedCost
                                            ? styles.actual_cost
                                            : styles.discounted_cost
                                    }
                                >
                                    {currentCost}
                                </div>
                                {currentDiscountedCost && (
                                    <div className={styles.discounted_cost}>
                                        {currentDiscountedCost}
                                    </div>
                                )}

                                {currentCost != 'Free' && (
                                    <Typography variant="body-md-bold">
                                        {costText}
                                    </Typography>
                                )}
                            </div>
                        </div>
                    )}
                    <button
                        onClick={() => {
                            console.log(statsigEventName);
                            logEvent(statsigEventName);
                            window.location.href = cta.link;
                        }}
                        data-heap-id={cta.heapId}
                        className={
                            isRecommended
                                ? styles.recommended_cta_button
                                : styles.cta_button
                        }
                    >
                        {isRecommended && (
                            <SparklingSvg height={18} width={18} color="#fff" />
                        )}
                        <Typography
                            variant="body-md-bold"
                            color={isRecommended ? '#fff' : '#13002D'}
                        >
                            {cta.text}
                        </Typography>
                    </button>
                </div>
            </div>
        </div>
    );
};
